import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { AssistantsSidebar, AssistantDetails, EditAssistantModal } from '../../components/assistants';
import { ConfirmModal } from '../../components/ui';
import { mockCategories } from '../../data/mockData';
import { api } from '../../services/api';
import type { Assistant } from '../../types';
import { sanitizeBrandText } from '../../utils/helpers';

export const AssistantDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [assistant, setAssistant] = useState<Assistant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load assistant from API
  const loadAssistant = useCallback(async () => {
    if (!id) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getAssistant(id);
      if (response.success && response.data) {
        const a = response.data as any;
        const mappedAssistant: Assistant = {
          id: a.id,
          name: a.name,
          description: a.description,
          author: sanitizeBrandText(a.authorName || 'Unknown'),
          authorId: a.authorId,
          categories: a.categories || [],
          llmModel: a.llmModel || 'gpt-4o',
          systemInstructions: a.systemInstructions || '',
          conversationStarters: a.conversationStarters || [],
          isShared: a.visibility === 'public',
          likes: a.likes || 0,
          status: a.status || 'active',
          icon: a.icon,
          createdAt: a.createdAt,
          updatedAt: a.updatedAt,
          datasourceIds: a.datasourceIds || [],
          integrationIds: a.integrationIds || [],
        };
        setAssistant(mappedAssistant);
      } else {
        setError('Assistant not found');
      }
    } catch (err) {
      console.error('Failed to load assistant:', err);
      setError('Failed to load assistant');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAssistant();
  }, [loadAssistant]);

  if (isLoading) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading assistant...</p>
        </div>
      </AppLayout>
    );
  }

  if (error || !assistant) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Assistant not found</h1>
          <p>{error}</p>
          <button onClick={() => navigate('/assistants')}>
            Back to Assistants
          </button>
        </div>
      </AppLayout>
    );
  }

  const handleBack = () => {
    navigate('/assistants');
  };

  const handleChatNow = () => {
    navigate(`/?assistant=${assistant.id}`);
  };

  const handleLike = () => {
    console.log('Like assistant');
  };

  const handleDislike = () => {
    console.log('Dislike assistant');
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(assistant.id);
  };

  const handleCopyInstructions = () => {
    navigator.clipboard.writeText(assistant.systemInstructions);
  };

  const handleCopyDetailLink = () => {
    navigator.clipboard.writeText(assistant.accessLinks?.details || '');
  };

  const handleCopyChatLink = () => {
    navigator.clipboard.writeText(assistant.accessLinks?.chat || '');
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedData: Partial<Assistant>) => {
    if (!assistant) return;
    
    const response = await api.updateAssistant(assistant.id, updatedData);
    if (response.success && response.data) {
      // Reload assistant data to get the latest
      await loadAssistant();
    } else {
      throw new Error('Failed to update assistant');
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!assistant) return;
    
    setIsDeleting(true);
    try {
      const response = await api.deleteAssistant(assistant.id);
      if (response.success) {
        // Navigate back to assistants list after successful deletion
        navigate('/assistants');
      } else {
        console.error('Failed to delete assistant');
      }
    } catch (err) {
      console.error('Failed to delete assistant:', err);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Sidebar for navigation context
  const sidebar = (
    <AssistantsSidebar
      selectedCategory="marketplace"
      onCategoryChange={() => navigate('/assistants')}
      searchQuery=""
      onSearchChange={() => {}}
      categories={mockCategories}
      selectedCategories={[]}
      onCategoriesChange={() => {}}
      createdByMe={false}
      onCreatedByMeChange={() => {}}
    />
  );

  return (
    <AppLayout sidebar={sidebar}>
      <AssistantDetails
        assistant={assistant}
        onBack={handleBack}
        onChatNow={handleChatNow}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onLike={handleLike}
        onDislike={handleDislike}
        onCopyId={handleCopyId}
        onCopyInstructions={handleCopyInstructions}
        onCopyDetailLink={handleCopyDetailLink}
        onCopyChatLink={handleCopyChatLink}
      />
      
      {/* Edit Modal */}
      <EditAssistantModal
        isOpen={showEditModal}
        assistant={assistant}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Assistant"
        message={`Are you sure you want to delete "${assistant.name}"? This action cannot be undone. All associated chats and data will be permanently removed.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onClose={() => setShowDeleteModal(false)}
      />
    </AppLayout>
  );
};

export default AssistantDetailsPage;
